/**
 * 写真まとめて一括投稿ツールの地図プロット (issue #23、Phase C)。
 *
 * 取り込んだ写真の位置 (EXIF GPS / 手動ピン) を地図上にマーカー表示する。
 * `ModerationMap`(posts-moderation) と同じ imperative maplibre パターン:
 *   - GSI 淡色ラスタタイル (style version 8、pmtiles 非依存)
 *   - point ごとに maplibregl.Marker (points 変化で差分同期)
 *   - 選択中 point は別色 (amber) で強調、他は primary
 *   - points 変化時に fitBounds (1 点以上)
 *   - マーカークリックで onSelectPoint(id)
 *
 * jsdom / test 環境では WebGL が不完全なため、map 生成・操作は try/catch + window guard で
 * 防御し、import しても test が落ちないようにする (maplibre 操作はすべて try で包む)。
 */
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useEffect, useRef } from 'react';

/** マーカー色: 通常 = primary (#00B4D8)、選択中 = amber (#D97706)。 */
const MARKER_COLOR = '#00B4D8';
const MARKER_COLOR_SELECTED = '#D97706';

/** 日本全体のデフォルト中心 / zoom (points が無いとき)。 */
const DEFAULT_CENTER: [number, number] = [139.7, 35.68];
const DEFAULT_ZOOM = 5;

const GSI_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  sources: {
    gsi: {
      type: 'raster',
      tiles: ['https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png'],
      tileSize: 256,
      attribution: '地理院タイル',
    },
  },
  layers: [{ id: 'gsi', type: 'raster', source: 'gsi' }],
};

export interface PhotoMapPoint {
  id: string;
  latitude: number;
  longitude: number;
  selected?: boolean;
  /** 360度写真 (docs/27)。リング状のマーカーで区別する。 */
  panorama?: boolean;
}

export interface PhotosMapProps {
  points: PhotoMapPoint[];
  onSelectPoint?: (id: string) => void;
}

interface MarkerEntry {
  marker: maplibregl.Marker;
  el: HTMLDivElement;
  selected: boolean;
}

function applyMarkerStyle(el: HTMLDivElement, selected: boolean, panorama = false): void {
  const color = selected ? MARKER_COLOR_SELECTED : MARKER_COLOR;
  const size = selected ? 22 : 18;
  // 360度写真は「白い縁取りの太いリング」で平面写真 (塗りつぶし) と区別する。
  const fill = panorama ? 'white' : color;
  const border = panorama ? `4px solid ${color}` : '2px solid white';
  el.style.cssText =
    `width:${size}px;height:${size}px;border-radius:50%;background:${fill};` +
    `border:${border};box-shadow:0 1px 4px rgba(0,0,0,0.35);cursor:pointer;box-sizing:border-box;`;
}

export function PhotosMap({ points, onSelectPoint }: PhotosMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markersRef = useRef<Map<string, MarkerEntry>>(new Map());
  // onSelectPoint を ref 経由で参照 (マーカー再生成を props 変化で誘発しない)。
  const onSelectRef = useRef(onSelectPoint);
  useEffect(() => {
    onSelectRef.current = onSelectPoint;
  }, [onSelectPoint]);

  // 地図の初期化 (mount 時 1 回、unmount で map.remove())。
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!containerRef.current || mapRef.current) return;
    const markers = markersRef.current;
    try {
      const map = new maplibregl.Map({
        container: containerRef.current,
        style: GSI_STYLE,
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
      });
      map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
      map.on('load', () => {
        try {
          map.resize();
        } catch {
          /* jsdom: resize 不可 */
        }
      });
      mapRef.current = map;
    } catch {
      /* jsdom / WebGL 非対応: map 生成失敗は無視 (test 環境) */
    }
    return () => {
      try {
        mapRef.current?.remove();
      } catch {
        /* ignore */
      }
      mapRef.current = null;
      markers.clear();
    };
  }, []);

  // マーカーの差分同期 + fitBounds。
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const prev = markersRef.current;

    try {
      // 削除: points に無くなった id のマーカーを除去。
      for (const [id, entry] of prev) {
        if (!points.some((p) => p.id === id)) {
          entry.marker.remove();
          prev.delete(id);
        }
      }

      const bounds = new maplibregl.LngLatBounds();
      let hasPoint = false;

      for (const p of points) {
        if (!Number.isFinite(p.latitude) || !Number.isFinite(p.longitude)) continue;
        bounds.extend([p.longitude, p.latitude]);
        hasPoint = true;
        const selected = p.selected === true;

        const existing = prev.get(p.id);
        if (existing) {
          // 座標更新 (手動ピン移動に追従)。
          existing.marker.setLngLat([p.longitude, p.latitude]);
          // 選択状態が変わったら見た目だけ更新 (マーカー再生成は不要)。
          if (existing.selected !== selected) {
            applyMarkerStyle(existing.el, selected, p.panorama === true);
            existing.selected = selected;
          }
          continue;
        }

        const el = document.createElement('div');
        applyMarkerStyle(el, selected, p.panorama === true);
        el.addEventListener('click', (ev) => {
          ev.stopPropagation();
          onSelectRef.current?.(p.id);
        });
        const marker = new maplibregl.Marker({ element: el })
          .setLngLat([p.longitude, p.latitude])
          .addTo(map);
        prev.set(p.id, { marker, el, selected });
      }

      if (hasPoint) {
        map.fitBounds(bounds, { padding: 48, maxZoom: 16, duration: 0 });
      }
    } catch {
      /* jsdom / WebGL 非対応: マーカー操作失敗は無視 (test 環境) */
    }
  }, [points]);

  return (
    <div
      ref={containerRef}
      className="w-full h-80 rounded-lg border border-slate-200 overflow-hidden"
      data-testid="photos-map"
    />
  );
}
